import './RatioGoalConflictModal.scss';
import * as Dfns from 'date-fns/fp';
import { ReactNode } from 'react';
import { RatioGoalConflict } from '@blisspointmedia/bpm-types/dist/Pacing';
import { AlertDialog } from '@Components/AlertDialog/AlertDialog';
import { Intent } from '@Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';

interface RatioGoalConflictModalProps {
  conflicts: RatioGoalConflict[];
  onHide: () => void;
}

// yyyy-MM-dd → "Jul 1"; falls back to the raw string if it doesn't parse.
const shortDate = (iso: string): string => {
  const parsed = Dfns.parseISO(iso);
  return Number.isNaN(parsed.getTime()) ? iso : Dfns.format('MMM d', parsed);
};

// `ROAS (Purchases, GA4)` for KPI-scoped types; bare `CPC` for the delivery-only ones (no KPI/source).
// Distinguishes same-metric collisions that differ only by scope.
const conflictHeader = (conflict: RatioGoalConflict): string => {
  const metric = conflict.metric.toUpperCase();
  const qualifiers = [
    conflict.kpi,
    conflict.source
      ? conflict.source
          .split(',')
          .map(source => source.toUpperCase())
          .join(', ')
      : undefined
  ].filter((qualifier): qualifier is string => Boolean(qualifier));
  return qualifiers.length > 0 ? `${metric} (${qualifiers.join(', ')})` : metric;
};

const goalLabel = (metric: string, goal: RatioGoalConflict['goals'][number]): string => {
  const segments = Object.entries(goal.segments)
    .map(([dim, value]) => `${dim}: ${value}`)
    .join(', ');
  const dateRange = `${shortDate(goal.goalStart)}–${shortDate(goal.goalEnd)}`;
  return [`${metric.toUpperCase()} ${goal.goalValue}`, segments, dateRange]
    .filter(Boolean)
    .join(' · ');
};

/**
 * Alerts that two or more ratio goals collide — matching the same table rows at equal specificity,
 * so no single target wins and those rows render "--". Lists each colliding set of goals so the user
 * knows which to adjust in Goal Intake.
 */
export const RatioGoalConflictModal = ({ conflicts, onHide }: RatioGoalConflictModalProps) => {
  const body: ReactNode = (
    <div className='ratio-goal-conflict-body'>
      <p>
        Two or more ratio goals match the same rows at equal specificity, so no Goal or % to Goal is
        shown for them. Adjust the goals in Goal Intake to resolve:
      </p>
      {conflicts.map(conflict => (
        <div
          className='ratio-goal-conflict-group'
          key={`${conflict.metric}:${conflict.goals.map(g => g.originalGoalId).join(',')}`}
        >
          <div className='ratio-goal-conflict-group-header'>
            {conflictHeader(conflict)} — affects{' '}
            {conditionallyPluralizeWithCount(conflict.affectedSegments, 'row')}
          </div>
          <ul>
            {conflict.goals.map(goal => (
              <li key={goal.originalGoalId}>{goalLabel(conflict.metric, goal)}</li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  return (
    <AlertDialog
      show
      onHide={onHide}
      intent={Intent.WARNING}
      title='Overlapping ratio goals — no target shown'
      body={body}
    />
  );
};
